import { useState } from 'react'
import { Plus, Sparkles, Trash2 } from 'lucide-react'
import { Button, Switch, WORKER_ROLE_LABELS, type WorkerRole } from '../../core/index.js'
import { DEFAULT_SHORT_SHIFT_RULES, EXAMPLE_STAFFING_PLAN } from '../defaultSchedulingRules.js'
import type { FixedPosition, HppdTarget, SchedulingRules, ShiftTypeKey, ShortShiftRules, StaffingGridRow, StaffingPlan } from '../types.js'

/**
 * The Scheduling tab's staffing plan (census response plan F0): how a day's
 * census becomes seats. PPD is patient days (a day's census is its PPD) and
 * HPPD hours per patient day; an HPPD plan gives each role a target and
 * splits the day's hours across the shift types, a grid gives seats by band
 * of census. Fixed positions are added whatever the census; the minimum
 * staffing lines above act as floors. Part of the tab's draft and Save.
 */

const ROLES: readonly WorkerRole[] = ['RN', 'LPN', 'CNA']
/** The HPPD targets a plan takes: one per role, and `licensed` for LPN seats an RN may also fill (F5). */
const TARGET_KEYS: readonly (WorkerRole | 'licensed')[] = [...ROLES, 'licensed']
const TARGET_LABELS: Record<WorkerRole | 'licensed', string> = { RN: `RN · ${WORKER_ROLE_LABELS.RN}`, LPN: `LPN · ${WORKER_ROLE_LABELS.LPN}`, CNA: `CNA · ${WORKER_ROLE_LABELS.CNA}`, licensed: 'Licensed nurses (LPN seats an RN may fill)' }
const ROUNDING_TEXT: Record<StaffingPlan['rounding'], string> = { up: 'rounded up', nearest: 'rounded to the nearest', short_shift: 'in whole seats, with half a seat or more as a short shift' }
const INPUT_CLASS = 'rounded border border-marine/30 bg-white px-2 py-1 text-sm text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20'
const SHIFT_TYPE_LABELS: Record<ShiftTypeKey, string> = { day: 'Day', evening: 'Evening', night: 'Night' }

function numberOrUndefined(text: string): number | undefined {
  return text.trim() === '' ? undefined : Number(text)
}

/** Whether the plan reads as the example, field by field in a fixed order (a saved plan comes back from the store with its keys in any order). */
export function isExamplePlan(plan: StaffingPlan): boolean {
  const example = EXAMPLE_STAFFING_PLAN
  const sameHppd = (a: HppdTarget | null, b: HppdTarget | null): boolean =>
    a === b || (a !== null && b !== null && (['RN', 'LPN', 'CNA', 'licensed'] as const).every((role) => (a.byRole[role] ?? null) === (b.byRole[role] ?? null)) && (['day', 'evening', 'night'] as const).every((key) => a.shiftSplit[key] === b.shiftSplit[key]))
  return plan.method === example.method && sameHppd(plan.hppd, example.hppd) && plan.grid.length === 0 && plan.fixed.length === 0 && plan.rounding === example.rounding && plan.acuityFactor === example.acuityFactor && plan.forecastWeeks === example.forecastWeeks
}

interface FacilityStaffingPlanProps {
  rules: SchedulingRules
  onChange: (patch: Partial<SchedulingRules>) => void
}

export function FacilityStaffingPlan({ rules, onChange }: FacilityStaffingPlanProps) {
  const plan = rules.staffing ?? null
  const offered = rules.shiftTypes.filter((entry) => entry.enabled).map((entry) => entry.key)
  const set = (patch: Partial<StaffingPlan>): void => onChange({ staffing: { ...(plan ?? EXAMPLE_STAFFING_PLAN), ...patch } })
  // Set once the example is loaded here, so the button can say what it did; nothing is stored until Save Configuration.
  const [loaded, setLoaded] = useState(false)

  if (!plan) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4 py-1">
          <div className="text-xs text-ink-mid">No staffing plan. The census is recorded and shown, but nothing is required from it.</div>
          <Switch checked={false} onChange={() => onChange({ staffing: EXAMPLE_STAFFING_PLAN })} label="Staffing plan" />
        </div>
        <p className="text-[11px] text-ink-mid leading-relaxed">Switching it on starts from an example: 3.5 hours per patient day (0.5 RN, 0.6 LPN, 2.4 CNA), split 40 / 35 / 25 across day, evening and night. Replace the numbers with your facility assessment’s.</p>
      </div>
    )
  }

  const split = plan.hppd?.shiftSplit ?? EXAMPLE_STAFFING_PLAN.hppd!.shiftSplit
  const splitTotal = offered.reduce((sum, key) => sum + (split[key] ?? 0), 0)
  const setGridRow = (index: number, patch: Partial<StaffingGridRow>): void => set({ grid: plan.grid.map((row, at) => (at === index ? { ...row, ...patch } : row)) })
  const setGridSeat = (index: number, shiftType: ShiftTypeKey, role: WorkerRole, text: string): void => {
    const row = plan.grid[index]
    if (!row) {
      return
    }
    const value = numberOrUndefined(text)
    const { [role]: _dropped, ...others } = row.seats[shiftType] ?? {}
    setGridRow(index, { seats: { ...row.seats, [shiftType]: value === undefined ? others : { ...others, [role]: Math.max(0, Math.round(value)) } } })
  }
  const setFixed = (index: number, patch: Partial<FixedPosition>): void => set({ fixed: plan.fixed.map((position, at) => (at === index ? { ...position, ...patch } : position)) })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 py-1">
        <div className="text-xs text-ink-mid">A staffing plan turns each day’s census into the seats each shift needs.</div>
        <Switch checked onChange={() => onChange({ staffing: null })} label="Staffing plan" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-ink-mid">Method</span>
        <select aria-label="Staffing method" className={INPUT_CLASS} value={plan.method} onChange={(event) => set({ method: event.target.value as StaffingPlan['method'], ...(event.target.value === 'hppd' && !plan.hppd ? { hppd: EXAMPLE_STAFFING_PLAN.hppd } : {}), ...(event.target.value === 'grid' && plan.grid.length === 0 ? { grid: [{ censusFrom: 0, censusTo: 40, seats: {} }] } : {}) })}>
          <option value="hppd">Hours per patient day (HPPD)</option>
          <option value="grid">Staffing grid, by band of census</option>
        </select>
        <Button icon={Sparkles} variant="secondary" size="sm" disabled={isExamplePlan(plan)} onClick={() => { onChange({ staffing: EXAMPLE_STAFFING_PLAN }); setLoaded(true) }}>
          {isExamplePlan(plan) ? 'The example is loaded' : 'Load the example'}
        </Button>
        {loaded && isExamplePlan(plan) && <span className="text-[11px] text-ink-mid">Loaded. Press Save Configuration to keep it.</span>}
      </div>

      {plan.method === 'hppd' && (
        <div>
          <div className="text-xs font-bold text-ink">HPPD target by role</div>
          <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">Productive nursing hours per patient day each role should reach. A day’s hours for a role are its PPD (the census) × this number; seats are those hours over the shift’s productive length (less any unpaid break), {ROUNDING_TEXT[plan.rounding]}. Leave a role blank to require none of it. The licensed target is for facilities that budget licensed nurses together: it asks for LPN seats, which an RN may fill where Details › Who may cover whom says so.</p>
          <div className="flex flex-wrap gap-4">
            {TARGET_KEYS.map((key) => (
              <label key={key} className="flex items-center gap-2 text-xs text-ink-mid">
                {TARGET_LABELS[key]}
                <input type="number" step={0.1} min={0} max={24} aria-label={`${key === 'licensed' ? 'Licensed nurse' : key} hours per patient day`} className={`${INPUT_CLASS} w-20 text-right font-bold`} value={plan.hppd?.byRole[key] ?? ''} onChange={(event) => {
                  const value = numberOrUndefined(event.target.value)
                  const { [key]: _dropped, ...others } = plan.hppd?.byRole ?? {}
                  const byRole: HppdTarget['byRole'] = value === undefined ? others : { ...others, [key]: Math.max(0, value) }
                  set({ hppd: { shiftSplit: split, byRole } })
                }} />
              </label>
            ))}
          </div>
          <div className="mt-3 text-xs font-bold text-ink">Split across the shifts</div>
          <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">The percent of the day’s hours each shift type carries. Over the offered shift types it must sum to 100 (now {splitTotal}).</p>
          <div className="flex flex-wrap gap-4">
            {offered.map((key) => (
              <label key={key} className="flex items-center gap-2 text-xs text-ink-mid">
                {SHIFT_TYPE_LABELS[key]}
                <input type="number" min={0} max={100} aria-label={`${SHIFT_TYPE_LABELS[key]} share of the day's hours`} className={`${INPUT_CLASS} w-16 text-right font-bold`} value={split[key]} onChange={(event) => set({ hppd: { byRole: plan.hppd?.byRole ?? {}, shiftSplit: { ...split, [key]: Math.max(0, Math.min(100, Number(event.target.value) || 0)) } } })} />
                <span>%</span>
              </label>
            ))}
            {Math.abs(splitTotal - 100) > 0.01 && <span className="text-[11px] text-coral self-center">Sums to {splitTotal}, not 100.</span>}
          </div>
        </div>
      )}

      {plan.method === 'grid' && (
        <div>
          <div className="text-xs font-bold text-ink">Staffing grid</div>
          <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">Each band of residents needs these seats per shift type and role. Bands must not overlap; a census above the last band uses it and is flagged.</p>
          <div className="overflow-x-auto">
            <table className="text-[11px]">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wide text-marine">
                  <th className="py-1 pr-2">Residents</th>
                  {offered.map((key) => ROLES.map((role) => (
                    <th key={`${key}-${role}`} className="py-1 px-1 text-center">
                      {SHIFT_TYPE_LABELS[key]} {role}
                    </th>
                  )))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {plan.grid.map((row, index) => (
                  <tr key={index}>
                    <td className="py-0.5 pr-2 whitespace-nowrap">
                      <input type="number" min={0} aria-label="Band from" className={`${INPUT_CLASS} w-16 text-right`} value={row.censusFrom} onChange={(event) => setGridRow(index, { censusFrom: Math.max(0, Math.round(Number(event.target.value) || 0)) })} />
                      <span className="mx-1 text-ink-mid">to</span>
                      <input type="number" min={0} aria-label="Band to" className={`${INPUT_CLASS} w-16 text-right`} value={row.censusTo} onChange={(event) => setGridRow(index, { censusTo: Math.max(0, Math.round(Number(event.target.value) || 0)) })} />
                    </td>
                    {offered.map((key) => ROLES.map((role) => (
                      <td key={`${key}-${role}`} className="px-1 py-0.5">
                        <input type="number" min={0} aria-label={`${SHIFT_TYPE_LABELS[key]} ${role} seats for ${row.censusFrom} to ${row.censusTo}`} className={`${INPUT_CLASS} w-14 text-right`} value={row.seats[key]?.[role] ?? ''} onChange={(event) => setGridSeat(index, key, role, event.target.value)} />
                      </td>
                    )))}
                    <td>
                      <button type="button" aria-label="Remove this band" className="p-1.5 text-ink-mid hover:text-coral" onClick={() => set({ grid: plan.grid.filter((_, at) => at !== index) })}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button icon={Plus} variant="secondary" size="sm" className="mt-2" onClick={() => set({ grid: [...plan.grid, { censusFrom: (plan.grid.at(-1)?.censusTo ?? -1) + 1, censusTo: (plan.grid.at(-1)?.censusTo ?? -1) + 5, seats: {} }] })}>
            Add a band
          </Button>
        </div>
      )}

      <div>
        <div className="text-xs font-bold text-ink">Fixed positions</div>
        <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">Seats needed whatever the census: the charge nurse on every shift, the RN on site. Added on top of the census-driven seats.</p>
        {plan.fixed.length === 0 && <p className="text-[11px] text-ink-mid">None.</p>}
        {plan.fixed.map((position, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2 py-1">
            <input type="number" min={1} aria-label="How many" className={`${INPUT_CLASS} w-14 text-right font-bold`} value={position.count} onChange={(event) => setFixed(index, { count: Math.max(1, Math.round(Number(event.target.value) || 1)) })} />
            <select aria-label="Role" className={INPUT_CLASS} value={position.role} onChange={(event) => setFixed(index, { role: event.target.value as WorkerRole })}>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            <span className="text-xs text-ink-mid">on every</span>
            <select aria-label="Shift type" className={INPUT_CLASS} value={position.shiftType} onChange={(event) => setFixed(index, { shiftType: event.target.value as ShiftTypeKey })}>
              {(offered.length > 0 ? offered : (['day', 'evening', 'night'] as const)).map((key) => (
                <option key={key} value={key}>
                  {SHIFT_TYPE_LABELS[key]}
                </option>
              ))}
            </select>
            <input aria-label="Label" placeholder="charge nurse" className={`${INPUT_CLASS} w-36`} value={position.label ?? ''} onChange={(event) => setFixed(index, { label: event.target.value === '' ? undefined : event.target.value })} />
            <button type="button" aria-label="Remove this position" className="p-1.5 text-ink-mid hover:text-coral" onClick={() => set({ fixed: plan.fixed.filter((_, at) => at !== index) })}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button icon={Plus} variant="secondary" size="sm" className="mt-2" onClick={() => set({ fixed: [...plan.fixed, { shiftType: offered[0] ?? 'day', role: 'RN', count: 1, label: 'charge nurse' }] })}>
          Add a position
        </Button>
      </div>

      <div className="flex flex-wrap gap-6 text-xs text-ink-mid">
        <label className="flex items-center gap-2">
          Rounding
          <select aria-label="Rounding" className={INPUT_CLASS} value={plan.rounding} onChange={(event) => set({ rounding: event.target.value as StaffingPlan['rounding'] })}>
            <option value="up">Up: a fraction of a person is a person</option>
            <option value="nearest">To the nearest seat</option>
            <option value="short_shift">Whole seats, half or more as a short shift</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          Acuity factor
          <input type="number" step={0.1} min={0.5} max={3} aria-label="Acuity factor" className={`${INPUT_CLASS} w-16 text-right font-bold`} value={plan.acuityFactor} onChange={(event) => set({ acuityFactor: Math.min(3, Math.max(0.5, Number(event.target.value) || 1)) })} />
        </label>
        <label className="flex items-center gap-2">
          Forecast from
          <input type="number" min={1} max={12} aria-label="Weeks the forecast averages" className={`${INPUT_CLASS} w-14 text-right font-bold`} value={plan.forecastWeeks} onChange={(event) => set({ forecastWeeks: Math.min(12, Math.max(1, Math.round(Number(event.target.value) || 4))) })} />
          weeks of the same weekday
        </label>
      </div>

      {plan.rounding === 'short_shift' && <ShortShiftSettings value={plan.shortShift ?? DEFAULT_SHORT_SHIFT_RULES} offered={offered} onChange={(shortShift) => set({ shortShift })} />}
    </div>
  )
}

/**
 * Where a fraction of a seat goes under short-shift rounding (F5, the SNF guide): one short shift of this length
 * once the fraction reaches the threshold, at the busiest hours of each shift type - a night's half seat usually
 * goes to the morning, so a start may sit outside the shift type's own hours.
 */
function ShortShiftSettings({ value, offered, onChange }: { value: ShortShiftRules; offered: readonly ShiftTypeKey[]; onChange: (value: ShortShiftRules) => void }) {
  return (
    <div>
      <div className="text-xs font-bold text-ink">Short shifts</div>
      <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">A shift needing 10.5 seats gets 10 and a short shift; one needing 10.4 gets 10, the fraction dropped. Put the short shift where residents need the most hands: mornings for care and evenings for meals and bedtime. Check the minimum staffing still holds.</p>
      <div className="flex flex-wrap gap-4 text-xs text-ink-mid">
        <label className="flex items-center gap-2">
          Length
          <input type="number" min={1} max={12} step={0.5} aria-label="Short shift length" className={`${INPUT_CLASS} w-16 text-right font-bold`} value={value.hours} onChange={(event) => onChange({ ...value, hours: Math.min(12, Math.max(1, Number(event.target.value) || 4)) })} />
          hours
        </label>
        <label className="flex items-center gap-2">
          From
          <input type="number" min={0.1} max={0.99} step={0.05} aria-label="Fraction of a seat that adds a short shift" className={`${INPUT_CLASS} w-16 text-right font-bold`} value={value.threshold} onChange={(event) => onChange({ ...value, threshold: Math.min(0.99, Math.max(0.1, Number(event.target.value) || 0.5)) })} />
          of a seat
        </label>
        {offered.map((key) => (
          <label key={key} className="flex items-center gap-2">
            {SHIFT_TYPE_LABELS[key]}’s starts at
            <input type="time" aria-label={`${SHIFT_TYPE_LABELS[key]} short shift starts`} className={`${INPUT_CLASS} w-24`} value={value.start[key]} onChange={(event) => onChange({ ...value, start: { ...value.start, [key]: event.target.value } })} />
          </label>
        ))}
      </div>
    </div>
  )
}
